import { t } from '@lingui/macro';
import { intersection, isEqual } from 'lodash';
import { Laptop } from '@material-ui/icons';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    AGENT_HARDWARE_2K_MODEL,
    AGENT_LATEST_AGENT_VERSION,
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_MODEL_SHORT_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NETWORK_IP_EXTERNAL_FIELD,
    AGENT_NETWORK_IP_EXTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_LOCAL_FIELD,
    AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV6_LOCAL_FIELD,
    AGENT_NETWORK_MAC_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
    AGENT_TAGS_FIELD,
    AGENT_TEST_COUNT_DISPLAY_FIELD,
    AGENT_TEST_COUNT_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TYPE_ICON_FIELD,
    AGENT_VIRTUAL_2K_MODEL,
    AGENTS_FUNCTION_NAME,
} from '../../pages-admin/agents/AgentConstants';
import { renderStatus, renderTestCount } from '../../pages-admin/agents/AgentRenderHelper';
import { NPoint2K, NPoint3K, NPoint4K, NPoint4KW } from '../../icon';
import { formatTags } from '../../../utilities/StringUtilities';
import { SEM_FIELD } from '../../pages-admin/service-tests/ServiceTestAdminConstants';
import { getAgentTypeIcon, getAgentTypeTooltip } from '../../pages-admin/agents/AgentListHelper';
import { buildPageTarget } from '../../pages-admin/AdminLinkUtils';
import {
    getAgentVoIPRegistrationInterface,
    getAgentVoIPRegistrationInterfaceTooltip,
} from '../../pages-admin/agents/AgentHelper';

/**
 * Gets the correct agent icon using API agent data
 * We have to do this rather than using the LegacyIcon component because this page uses V3 to get agent
 * data, which does not return icon data.
 * @param {Object} agentData
 * @returns agent icon
 */
function getAgentIconFromGeneration(agentData) {
    switch (agentData.generation) {
        case 2:
            return <NPoint2K />;
        case 3:
            return <NPoint3K />;
        case 4:
            return agentData.hasWireless ? <NPoint4KW /> : <NPoint4K />;
        default:
            return <Laptop />;
    }
}

export const SERVICE_TEST_DATA_ARRAY_SEARCH_FIELDS = [
    AGENT_NAME_FIELD,
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
];

export function buildServiceTestAgentPickerGridColumns(classes, showVoipRegistration = false) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 80,
            sortable: true,
            mapping: AGENT_TYPE_ICON_FIELD,
            csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
            className: classes.root,
            icons: [
                {
                    getIcon: getAgentTypeIcon,
                    getTooltip: getAgentTypeTooltip,
                },
            ],
        },
        {
            type: COLUMN_TYPES.LINK,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
            link: {
                target: buildPageTarget(AGENTS_FUNCTION_NAME),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 84,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`VoIP Interface`,
            sortable: false,
            renderer: (row) => getAgentVoIPRegistrationInterface(row),
            getTooltip: (agent) => getAgentVoIPRegistrationInterfaceTooltip(agent),
            hidden: !showVoipRegistration,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Service Tests`,
            sortable: true,
            sortProperty: AGENT_TEST_COUNT_FIELD,
            mapping: AGENT_TEST_COUNT_DISPLAY_FIELD,
            className: classes.root,
            renderer: renderTestCount,
            width: 145,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SEM_FIELD,
            title: t`Smart Edge Monitoring`,
            align: 'center',
            sortable: true,
            formatter: (value) => {
                return value === true ? t`On` : t`Off`;
            },
            width: 195,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: AGENT_TAGS_FIELD,
            title: t`Tags`,
            sortable: true,
            formatter: formatTags,
            width: 200,
        },
    ];
}

export function buildPopUpServiceTestAgentPickerGridColumns(classes, showVoipRegistration = false) {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 80,
            sortable: true,
            mapping: AGENT_TYPE_ICON_FIELD,
            csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
            className: classes.root,
            icons: [
                {
                    getIcon: getAgentTypeIcon,
                    getTooltip: getAgentTypeTooltip,
                },
            ],
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 84,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`VoIP Interface`,
            sortable: false,
            renderer: (row) => getAgentVoIPRegistrationInterface(row),
            getTooltip: (agent) => getAgentVoIPRegistrationInterfaceTooltip(agent),
            hidden: !showVoipRegistration,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Service Tests`,
            sortable: true,
            sortProperty: AGENT_TEST_COUNT_FIELD,
            mapping: AGENT_TEST_COUNT_DISPLAY_FIELD,
            className: classes.root,
            renderer: renderTestCount,
            width: 145,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: SEM_FIELD,
            title: t`Smart Edge Monitoring`,
            align: 'center',
            sortable: true,
            formatter: (value) => {
                return value === true ? t`On` : t`Off`;
            },
            width: 195,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: AGENT_TAGS_FIELD,
            title: t`Tags`,
            sortable: true,
            formatter: formatTags,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IP_EXTERNAL_FIELD,
            sortProperty: AGENT_NETWORK_IP_EXTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IPv4`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IPv6`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV6_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
            width: 300,
        },
    ];
}

/**
 * Column builder for agent selector and matched tag agents data tables
 * @returns array of data table columns for agents
 */
export function buildNetTestNowAgentPickerGridColumns() {
    return [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 80,
            mapping: 'agent.generation',
            icons: [
                {
                    getIcon: (row) => getAgentIconFromGeneration(row),
                    getTooltip: (row) => row[AGENT_TRANSLATED_TYPE_FIELD],
                },
            ],
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 84,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`Test Count`,
            sortable: true,
            mapping: AGENT_TEST_COUNT_DISPLAY_FIELD,
            width: 120,
        },
        {
            type: COLUMN_TYPES.TEXT,
            mapping: AGENT_TAGS_FIELD,
            title: t`Tags`,
            sortable: false,
            formatter: formatTags,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Version`,
            sortable: true,
            mapping: AGENT_LATEST_AGENT_VERSION,
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
            width: 140,
        },
    ];
}

export function buildNetTestNowAgentTagGridColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 85,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Admin MAC`,
            sortable: true,
            mapping: AGENT_NETWORK_MAC_FIELD,
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
            width: 140,
        },
    ];
}

/**
 * Returns a list of agents filtered by the selected tags and tag logic
 * @param {object[]} agentList
 * @param {string[]} selectedTags
 * @param {'all' | 'any'} tagLogic
 * @returns
 */
export function filterAgentsBySelectedTags(agentList, selectedTags, tagLogic) {
    return tagLogic === 'all'
        ? agentList
              .filter((agentData) => agentData.tags.length > 0)
              .filter((agentData) => {
                  const commonTags = intersection(agentData.tags, selectedTags);
                  return selectedTags.length > 0 && isEqual(selectedTags, commonTags);
              })
        : agentList.filter((agentData) => selectedTags.some((submittedTag) => agentData.tags.includes(submittedTag)));
}

export function isWirelessAgent(agent) {
    return agent.generation > 2 && agent.hasWireless && agent.isWirelessEnabled === true;
}

export function is2KAgent(agent) {
    return (
        agent[AGENT_MODEL_SHORT_FIELD] === AGENT_HARDWARE_2K_MODEL ||
        agent[AGENT_MODEL_SHORT_FIELD] === AGENT_VIRTUAL_2K_MODEL
    );
}
